"use client"; 

import Grid from '../components/grid';
import Text from '../components/ui/text';
import React, { ReactElement, useState } from 'react';
import Link from 'next/link';
import Divider from './Divider';
import { useSnapshot } from 'valtio';
import { formStore } from '@store/form-store';
import { useForm } from 'react-hook-form';
import { onSubmitForm, TFormFields } from '@telesoft/lib/web/onSubmitForm';
import SubmittedState from '../components/ui/submitted-state';
import FormLoadingOverlay from '../components/ui/form-loading-overlay';
import BrowserSelect from '../components/ui/browser-select';
import {
  OPT_IN_PREFERENCES,
  PRODUCT_OPTIONS,
  REGION_OPTIONS,
  SEVERITY_OPTIONS,
} from '@telesoft/lib/web/constants';
import Input from '../components/ui/input';
import FormSubmitButton from '../components/ui/form-submit-button';
import TermsCluster from '../components/common/modal/terms-cluster';

export default function SupportForm(): ReactElement {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const snap = useSnapshot(formStore);
  const isLoading = snap.isLoading;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<TFormFields>();

  if (
    snap.fields.firstName &&
    snap.fields.lastName &&
    snap.fields.email &&
    snap.fields.phone
  ) {
    setValue('firstName', snap.fields.firstName);
    setValue('lastName', snap.fields.lastName);
    setValue('email', snap.fields.email);
    setValue('phone', snap.fields.phone);
  }

  if (snap.fields.company) {
    setValue('company', snap.fields.company);
  }

  async function onSubmit(fields: TFormFields) {
    onSubmitForm(
      'Support Request Form',
      false,
      fields,
      'support-requests',
      setIsSubmitted,
      reset
    );
  }

  return (
    <Grid>
      <div className="col-span-full bg-white p-8 md:p-12 lg:p-20 flex flex-col">
        <Grid className="gap-0 place-content-center lg:gap-x-8">
          <div className="flex flex-col col-span-full md:col-span-4 lg:col-span-6 xl:col-span-7">
            <h5 className="mb-1 lg:mb-2 text-opacity-50">
              SUPPORT PLAN MEMBERS
            </h5>
            <Text variant="subHeading">Create a new support request</Text>
          </div>

          <div
            className={`h-[1px] bg-black col-span-full md:col-span-8 lg:col-span-6 xl:col-span-full my-4 md:my-6 lg:my-8 xl:my-10`}
          ></div>
        </Grid>

        <div className="flex flex-col text-black md:mt-8 lg:mt-0">
          <Text variant="heading">
            For critical issues please call your local Telesoft office.
          </Text>
          <Text variant="body" className="w-full">
            <br /> For issues within the scope of a Telesoft{' '}
            <Link href="/support" className="border-b border-telesoft">
              
                Service Level Support Agreement
              
            </Link>
            , please complete the following form so that we have the details we
            need to respond quickly.
          </Text>
        </div>

        <Divider />
        {isSubmitted && <SubmittedState />}

        {!isSubmitted && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col flex-grow justify-center"
            noValidate
          >
            <div className="flex flex-col space-y-4">
              <fieldset className="relative space-y-4 mb-8">
                <FormLoadingOverlay show={isLoading} />
                <BrowserSelect
                  options={SEVERITY_OPTIONS}
                  labelKey="Assessment of Severity*"
                  {...register('severityLevel', {
                    required: 'Assessment of severity is required',
                  })}
                  errorKey={errors.severityLevel?.message}
                />

                <BrowserSelect
                  options={REGION_OPTIONS}
                  labelKey="Region*"
                  {...register('region', {
                    required: `Please select a region`,
                  })}
                  errorKey={errors.region?.message}
                />

                <Input
                  labelKey="First Name*"
                  type="text"
                  variant="solid"
                  {...register('firstName', {
                    required: 'Your first name is required',
                  })}
                  errorKey={errors.firstName?.message}
                />

                <Input
                  labelKey="Last Name*"
                  type="text"
                  variant="solid"
                  {...register('lastName', {
                    required: 'Your last name is required',
                  })}
                  errorKey={errors.lastName?.message}
                />

                <Input
                  labelKey="Email*"
                  type="email"
                  variant="solid"
                  {...register('email', {
                    required: `Email is required`,
                    pattern: {
                      value:
                        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: 'Please use a valid email',
                    },
                  })}
                  errorKey={errors.email?.message}
                />

                <Input
                  labelKey="Telephone*"
                  type="text"
                  variant="solid"
                  {...register('phone', {
                    required: `Your telephone is required`,
                  })}
                  errorKey={errors.phone?.message}
                />

                <Input
                  labelKey="Company*"
                  type="text"
                  variant="solid"
                  {...register('company', {
                    required: `Your company name is required`,
                  })}
                  errorKey={errors.company?.message}
                />

                <Input
                  labelKey="Job Title And Department*"
                  type="text"
                  variant="solid"
                  {...register('jobTitle', {
                    required: `Your job title is required`,
                  })}
                  errorKey={errors.jobTitle?.message}
                />

                <Input
                  labelKey="Please Describe the problem you're facing*"
                  type="textarea"
                  variant="solid"
                  {...register('description')}
                  errorKey={errors.description?.message}
                />

                <BrowserSelect
                  options={PRODUCT_OPTIONS}
                  labelKey="Product*"
                  {...register('productName', {
                    required: `Please select aa product`,
                  })}
                  errorKey={errors.productName?.message}
                />

                <Input
                  labelKey="Software Name And Release Version"
                  type="text"
                  variant="solid"
                  {...register('software')}
                  errorKey={errors.software?.message}
                />

                <Input
                  labelKey="Geographical Location of Hardware (if applicable)"
                  type="text"
                  variant="solid"
                  {...register('hardware')}
                  errorKey={errors.hardware?.message}
                />

                <Input
                  labelKey="Hardware Serial Number"
                  type="text"
                  variant="solid"
                  {...register('serial')}
                  errorKey={errors.serial?.message}
                />

                <BrowserSelect
                  options={OPT_IN_PREFERENCES}
                  labelKey="I prefer to me contacted via:"
                  {...register('contactPreference')}
                  errorKey={errors.contactPreference?.message}
                />
              </fieldset>
              <FormSubmitButton isLoading={isLoading} />
              <TermsCluster />
            </div>
          </form>
        )}
      </div>
    </Grid>
  );
}
